import { ChevronDownIcon } from 'lucide-react'

import { Checkbox } from '@/components/ui/checkbox'
import { DataTableColumnHeader } from '@/components/ui/data-table-column-header'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { TableColumn } from '@/models'
import { PipelineItem } from '@/services/sandbox.service'
import { unsensitiveFilter } from '@/utils'

export const AppSandboxRequestsTableColumns: TableColumn<PipelineItem>[] = [
  {
    id: 'select',
    size: 30,
    header: ({ table }) => (
      <div className="flex items-center space-x-2">
        <Checkbox
          aria-label="Select all in the page"
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <div className="cursor-pointer">
              <ChevronDownIcon className="w-4 h-4" />
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="flex items-center w-56">
            <Checkbox
              aria-label="Select all"
              checked={table.getIsAllRowsSelected()}
              className="mr-2"
              id="select-all"
              onCheckedChange={(value) => table.toggleAllRowsSelected(!!value)}
            />
            <span>Select all items</span>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    ),
    cell: ({ row }) => (
      <Checkbox
        aria-label="Select row"
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
      />
    ),
    enableSorting: false,
    enableHiding: false,
    enableResizing: false,
    enableGlobalFilter: false,
    enableColumnFilter: false
  },
  {
    id: 'fullname',
    accessorKey: 'fullname',
    displayName: 'User name',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="User Name" />
    ),
    enableResizing: false,
    filterFn: unsensitiveFilter,
    sortingFn: (a, b) => {
      const idA = a.original.pipeline_id
      const idB = b.original.pipeline_id

      if (!idA && !idB) {
        return 0
      } else if (!idA) {
        return 1
      } else if (!idB) {
        return -1
      }

      return parseInt(idA) - parseInt(idB)
    }
  },
  {
    id: 'company_name',
    accessorKey: 'company_name',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Company name" />
    ),
    displayName: 'Company name',
    enableResizing: true,
    sortUndefined: 1,
    filterFn: unsensitiveFilter
  },
  {
    id: 'email',
    accessorKey: 'email',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Email" />
    ),
    displayName: 'Email',
    enableResizing: true,
    sortUndefined: 1,
    filterFn: unsensitiveFilter
  },
  {
    id: 'position',
    accessorKey: 'position',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Position" />
    ),
    displayName: 'Position',
    enableResizing: true,
    sortUndefined: 1,
    filterFn: unsensitiveFilter
  },
  {
    id: 'sandbox_usecase',
    accessorKey: 'sandbox_usecase',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Sandbox Usecase" />
    ),
    displayName: 'Sandbox Usecase',
    enableResizing: true,
    sortUndefined: 1,
    filterFn: unsensitiveFilter
  },
  {
    id: 'connected_to_existing_epilot_customer',
    accessorKey: 'connected_to_existing_epilot_customer',
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Connected to existing Epilot customer"
      />
    ),
    displayName: 'Connected to existing Epilot customer',
    enableResizing: true,
    sortUndefined: 1,
    filterFn: unsensitiveFilter
  },
  {
    id: 'requested_at',
    accessorKey: 'requested_at',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Requested at" />
    ),
    accessorFn: (row) => (row.requested_at ? row.requested_at : undefined),
    cell: ({ row }) => {
      if (!row.original.requested_at) {
        return ''
      }

      const date = new Date(row.original.requested_at)

      return date.toLocaleString()
    },
    sortingFn: (a, b) => {
      if (!a.original.created_at && !b.original.created_at) {
        return 0
      }

      if (!a.original.created_at) {
        return 1
      }

      if (!b.original.created_at) {
        return -1
      }

      const dateA = new Date(a.original.created_at)
      const dateB = new Date(b.original.created_at)

      if (dateA === null && dateB === null) {
        return 0
      } else if (dateA === null) {
        return 1
      } else if (dateB === null) {
        return -1
      }

      return dateA.getTime() - dateB.getTime()
    },
    displayName: 'Requested at',
    sortUndefined: -1,
    enableGlobalFilter: false,
    enableColumnFilter: false
  }
]
